/**
 * useAMMessages — Real-time AM-to-Client chat hook
 *
 * Manages message fetching, sending, read receipts, and real-time
 * subscriptions for the AM chat system. Used by both client-side
 * (AccountManagerChat) and AM-side (ClientChat) components.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface AMMessage {
  id: string;
  am_id: string;
  agent_id: string;
  sender_role: "am" | "client";
  sender_id: string;
  body: string;
  read_at: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  attachments?: AMMessageAttachment[];
}

export interface AMMessageAttachment {
  id: string;
  message_id: string;
  file_name: string;
  file_url: string;
  mime_type: string;
  size_bytes: number;
  thumbnail_url: string | null;
  created_at: string;
}

export interface AMConversation {
  agent_id: string;
  am_id: string;
  agent_name: string;
  agent_company: string | null;
  last_message: string;
  last_message_at: string;
  unread_count: number;
}

interface UseAMMessagesOptions {
  amId: string;
  agentId: string;
  senderRole: "am" | "client";
  senderId: string;
  enabled?: boolean;
}

export function useAMMessages({
  amId,
  agentId,
  senderRole,
  senderId,
  enabled = true,
}: UseAMMessagesOptions) {
  const [messages, setMessages] = useState<AMMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Fetch message history
  const fetchMessages = useCallback(async () => {
    if (!amId || !agentId || !enabled) return;

    const { data, error } = await supabase
      .from("am_messages")
      .select("*, am_message_attachments(*)")
      .eq("am_id", amId)
      .eq("agent_id", agentId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("[useAMMessages] Failed to fetch messages:", error);
    } else {
      const msgs = (data || []).map((m: any) => ({
        ...m,
        attachments: m.am_message_attachments || [],
      })) as AMMessage[];
      setMessages(msgs);
    }
    setLoading(false);
  }, [amId, agentId, enabled]);

  // Initial fetch
  useEffect(() => {
    if (enabled) {
      setLoading(true);
      fetchMessages();
    }
  }, [fetchMessages, enabled]);

  // Real-time subscription
  useEffect(() => {
    if (!amId || !agentId || !enabled) return;

    const channel = supabase
      .channel(`am-chat-${amId}-${agentId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "am_messages",
          filter: `agent_id=eq.${agentId}`,
        },
        (payload) => {
          const newMsg = payload.new as AMMessage;
          // Only add if it belongs to this AM conversation
          if (newMsg.am_id === amId) {
            setMessages((prev) => {
              // Prevent duplicates
              if (prev.some((m) => m.id === newMsg.id)) return prev;
              return [...prev, newMsg];
            });
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "am_messages",
          filter: `agent_id=eq.${agentId}`,
        },
        (payload) => {
          const updated = payload.new as AMMessage;
          if (updated.am_id === amId) {
            setMessages((prev) =>
              prev.map((m) => (m.id === updated.id ? updated : m))
            );
          }
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [amId, agentId, enabled]);

  // Send a message (max 2000 chars)
  const sendMessage = useCallback(
    async (body: string) => {
      const trimmed = body.trim();
      if (!amId || !agentId || !trimmed) return null;
      if (trimmed.length > 2000) {
        console.error("[useAMMessages] Message exceeds 2000 character limit");
        return null;
      }

      setSending(true);
      const { data, error } = await supabase
        .from("am_messages")
        .insert({
          am_id: amId,
          agent_id: agentId,
          sender_role: senderRole,
          sender_id: senderId,
          body: body.trim(),
        })
        .select()
        .single();

      setSending(false);

      if (error) {
        console.error("[useAMMessages] Failed to send message:", error);
        return null;
      }
      return data as AMMessage;
    },
    [amId, agentId, senderRole, senderId]
  );

  // Mark messages as read
  const markAsRead = useCallback(async () => {
    if (!amId || !agentId || !enabled) return;

    // Mark unread messages from the OTHER side as read
    const otherRole = senderRole === "am" ? "client" : "am";

    await supabase
      .from("am_messages")
      .update({ read_at: new Date().toISOString() })
      .eq("am_id", amId)
      .eq("agent_id", agentId)
      .eq("sender_role", otherRole)
      .is("read_at", null);

    // Update local state
    setMessages((prev) =>
      prev.map((m) =>
        m.sender_role === otherRole && !m.read_at
          ? { ...m, read_at: new Date().toISOString() }
          : m
      )
    );
  }, [amId, agentId, senderRole, enabled]);

  // Send a message with file attachment
  const sendAttachment = useCallback(
    async (file: File) => {
      if (!amId || !agentId) return null;

      const MAX_SIZE = 10 * 1024 * 1024; // 10MB
      if (file.size > MAX_SIZE) {
        console.error("[useAMMessages] File exceeds 10MB limit");
        return null;
      }

      setSending(true);

      // 1. Upload file to Supabase Storage
      const ext = file.name.split(".").pop() || "bin";
      const path = `${amId}/${agentId}/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("am-attachments")
        .upload(path, file);

      if (uploadError) {
        console.error("[useAMMessages] File upload failed:", uploadError);
        setSending(false);
        return null;
      }

      const { data: urlData } = supabase.storage
        .from("am-attachments")
        .getPublicUrl(path);

      // 2. Create the message
      const { data: msgData, error: msgError } = await supabase
        .from("am_messages")
        .insert({
          am_id: amId,
          agent_id: agentId,
          sender_role: senderRole,
          sender_id: senderId,
          body: `📎 ${file.name}`,
        })
        .select()
        .single();

      if (msgError || !msgData) {
        console.error("[useAMMessages] Failed to send attachment message:", msgError);
        setSending(false);
        return null;
      }

      // 3. Create attachment record
      await supabase.from("am_message_attachments").insert({
        message_id: msgData.id,
        file_name: file.name,
        file_url: urlData.publicUrl,
        mime_type: file.type || "application/octet-stream",
        size_bytes: file.size,
      });

      setSending(false);
      return msgData as AMMessage;
    },
    [amId, agentId, senderRole, senderId]
  );

  const unreadCount = messages.filter(
    (m) => m.sender_role !== senderRole && !m.read_at
  ).length;

  return {
    messages,
    loading,
    sending,
    sendMessage,
    sendAttachment,
    markAsRead,
    unreadCount,
    refetch: fetchMessages,
  };
}

/**
 * useAMConversations — Fetch all conversations for an AM
 *
 * Returns a list of agent conversations with last message and unread count.
 * Used by the AM portal sidebar/inbox.
 */
export function useAMConversations(amId: string | undefined) {
  const [conversations, setConversations] = useState<AMConversation[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchConversations = useCallback(async () => {
    if (!amId) return;

    // Get all active assignments for this AM
    const { data: assignments } = await supabase
      .from("client_am_assignments")
      .select("agent_id")
      .eq("am_id", amId)
      .eq("status", "active");

    if (!assignments || assignments.length === 0) {
      setConversations([]);
      setLoading(false);
      return;
    }

    const agentIds = assignments.map((a) => a.agent_id);

    // Get agent info
    const { data: agents } = await supabase
      .from("agents")
      .select("id, name, company")
      .in("id", agentIds);

    const agentMap = new Map(
      (agents || []).map((a) => [a.id, { name: a.name, company: a.company }])
    );

    // Get messages grouped by agent
    const { data: msgs } = await supabase
      .from("am_messages")
      .select("agent_id, body, created_at, sender_role, read_at")
      .eq("am_id", amId)
      .in("agent_id", agentIds)
      .order("created_at", { ascending: false });

    // Build conversation list
    const convMap = new Map<
      string,
      { lastMsg: string; lastAt: string; unread: number }
    >();

    for (const msg of msgs || []) {
      if (!convMap.has(msg.agent_id)) {
        convMap.set(msg.agent_id, {
          lastMsg: msg.body,
          lastAt: msg.created_at,
          unread: 0,
        });
      }
      // Count unread messages from clients
      if (msg.sender_role === "client" && !msg.read_at) {
        const entry = convMap.get(msg.agent_id)!;
        entry.unread++;
      }
    }

    // Merge: all assigned agents (even those without messages yet)
    const convList: AMConversation[] = agentIds.map((agentId) => {
      const agent = agentMap.get(agentId);
      const conv = convMap.get(agentId);
      return {
        agent_id: agentId,
        am_id: amId,
        agent_name: agent?.name || "Unknown",
        agent_company: agent?.company || null,
        last_message: conv?.lastMsg || "",
        last_message_at: conv?.lastAt || "",
        unread_count: conv?.unread || 0,
      };
    });

    // Sort: unread first, then by last message time
    convList.sort((a, b) => {
      if (a.unread_count > 0 && b.unread_count === 0) return -1;
      if (b.unread_count > 0 && a.unread_count === 0) return 1;
      return (
        new Date(b.last_message_at || 0).getTime() -
        new Date(a.last_message_at || 0).getTime()
      );
    });

    setConversations(convList);
    setLoading(false);
  }, [amId]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Real-time: refresh on any new message
  useEffect(() => {
    if (!amId) return;

    const channel = supabase
      .channel(`am-conversations-${amId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "am_messages",
        },
        () => {
          fetchConversations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [amId, fetchConversations]);

  return { conversations, loading, refetch: fetchConversations };
}
